import { useCallback, useState } from "react";
import type { ThreadId } from "@t3tools/contracts";

import { apiUrl } from "../../apiBase";
import { runAgent, runWorkflow } from "../../morrowAgents";
import { threadEnvironment } from "~/state/threads";
import { useThreadRefs } from "~/state/entities";
import { useAtomCommand } from "~/state/use-atom-command";
import type { ActivityCardActionId } from "./activityCardActions";
import type { ActivityEvent, ActivityStatus } from "./activitiesTypes";

/** Resolve an allowance on the server. Returns true only when the server actually
 * recorded the decision ({ok:true}); on any failure we return false so the caller can
 * keep the card pending (the truth) rather than optimistically marking it resolved. */
async function postAllowanceResolve(id: string, decision: "approve" | "deny"): Promise<boolean> {
  try {
    const res = await fetch(apiUrl("/api/morrow/allowances/resolve"), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ id, decision }),
    });
    if (!res.ok) return false;
    const body = (await res.json().catch(() => null)) as { ok?: boolean } | null;
    return body?.ok === true;
  } catch {
    /* poll / signal will reconcile */
    return false;
  }
}

function patchStatus(status: ActivityStatus): Partial<ActivityEvent> {
  return { status };
}

/** Trigger the real backend for a "run now" / "retry": run the workflow or the agent behind
 * this event. Returns true if we actually kicked something off. */
async function triggerRun(event: ActivityEvent): Promise<boolean> {
  if (event.kind === "workflow_run" && event.refs.workflowId) {
    return runWorkflow(event.refs.workflowId);
  }
  if (event.refs.agentId && event.refs.agentId !== "main") {
    return runAgent(event.refs.agentId);
  }
  return false;
}

export function useActivityCardAction({
  mockMode,
  onReview,
}: {
  readonly mockMode: boolean;
  readonly onReview: (eventId: string) => void;
}) {
  const [patches, setPatches] = useState<Record<string, Partial<ActivityEvent>>>({});
  const [busy, setBusy] = useState<{ eventId: string; action: ActivityCardActionId } | null>(null);
  // Resolve a thread's environment so Cancel can interrupt the actual running turn.
  const threadRefs = useThreadRefs();
  const interruptTurn = useAtomCommand(threadEnvironment.interruptTurn, { reportFailure: false });

  const applyPatch = useCallback((eventId: string, patch: Partial<ActivityEvent>) => {
    setPatches((prev) => ({ ...prev, [eventId]: { ...prev[eventId], ...patch } }));
  }, []);

  const mergeEvents = useCallback(
    (events: readonly ActivityEvent[]) => events.map((e) => ({ ...e, ...patches[e.id] })),
    [patches],
  );

  const handleAction = useCallback(
    async (actionId: ActivityCardActionId, event: ActivityEvent) => {
      if (busy?.eventId === event.id) return;

      const review = () => onReview(event.id);

      switch (actionId) {
        case "review":
        case "view":
          review();
          return;
        case "approve":
        case "deny": {
          setBusy({ eventId: event.id, action: actionId });
          const decision = actionId === "approve" ? "approve" : "deny";
          // In mock mode there's no server; otherwise only mark the card resolved when the
          // server confirms it. On failure leave it pending so the real status shows.
          const resolved =
            mockMode || !event.refs.allowanceId
              ? true
              : await postAllowanceResolve(event.refs.allowanceId, decision);
          if (resolved) {
            applyPatch(event.id, patchStatus(actionId === "approve" ? "approved" : "denied"));
          }
          setBusy(null);
          return;
        }
        case "run_now":
        case "retry": {
          setBusy({ eventId: event.id, action: actionId });
          // Optimistic: show it running immediately; the 20s feed poll reconciles the real status.
          applyPatch(event.id, patchStatus("running"));
          if (!mockMode) await triggerRun(event).catch(() => false);
          setBusy(null);
          return;
        }
        case "cancel": {
          setBusy({ eventId: event.id, action: actionId });
          // The SERVER stop first: it marks the run stopped (so the walk starts nothing else)
          // and interrupts the run's thread itself, so the run ends whether or not that thread
          // is one this browser knows about. A workflow's own thread usually isn't in
          // `threadRefs`, and interrupting first left a window where the walk saw its step end,
          // found the run still "running" and went on to the next step.
          if (!mockMode && event.refs.runId) {
            await fetch(apiUrl("/api/morrow/workflows/runs/stop"), {
              method: "POST",
              headers: { "content-type": "application/json", accept: "application/json" },
              body: JSON.stringify({ runId: event.refs.runId }),
            }).catch(() => undefined);
          }
          const threadId = event.refs.threadId;
          if (!mockMode && threadId) {
            const ref = threadRefs.find((r) => r.threadId === threadId);
            if (ref) {
              await interruptTurn({
                environmentId: ref.environmentId,
                input: { threadId: threadId as ThreadId },
              }).catch(() => undefined);
            }
          }
          applyPatch(event.id, patchStatus("failed"));
          setBusy(null);
          return;
        }
        default:
          return;
      }
    },
    [applyPatch, busy?.eventId, interruptTurn, mockMode, onReview, threadRefs],
  );

  const isBusy = useCallback(
    (eventId: string, actionId: ActivityCardActionId) =>
      busy?.eventId === eventId && busy.action === actionId,
    [busy],
  );

  return { mergeEvents, handleAction, isBusy };
}
